import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerProps,
  SkeletonText,
  Stack,
  Text,
} from '@chakra-ui/react';

import { noop } from '@blockpulse3/data/shared';
import {
  IdentityType,
  SubscriptionInfosFragment,
  useGetSubscriptionQuery,
} from '@blockpulse3/graphql/hooks';
import { formatDate } from '@blockpulse3/helpers';
import {
  IdentityAvatar,
  IdentityCard,
  IdentityCardDescription,
  IdentityCardTitle,
} from '@blockpulse3/ui/commons';

import { DeleteSubscriptionButton } from './DeleteSubscriptionButton';
import { SubscriptionDetailsActivityFeed } from './SubscriptionDetailsActivityFeed';
import { SubscriptionDetailsComments } from './SubscriptionDetailsComments';
import { SubscriptionDetailsDocuments } from './SubscriptionDetailsDocuments';
import { SubscriptionDetailsHoldingMethod } from './SubscriptionDetailsHoldingMethod';
import { SubscriptionDetailsInvestAmount } from './SubscriptionDetailsInvestAmount';
import { SubscriptionDetailsReminder } from './SubscriptionDetailsReminder';
import { SubscriptionDetailsTags } from './SubscriptionDetailsTags';
import { SubscriptionProgressSteps } from './SubscriptionProgressSteps';

type Props = {
  subscriptionId: SubscriptionInfosFragment['id'];
  onDeleteRow?: () => void;
} & Omit<DrawerProps, 'children'>;

/**
 * SubscriberDetailsSidePanel.
 * Subscriber details drawer of the Fundraising.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriptionDetailsSidePanel({
  subscriptionId,
  onDeleteRow = noop,
  onClose,
  ...props
}: Props): JSX.Element {
  const { data, loading } = useGetSubscriptionQuery({ variables: { subscriptionId } });
  const subscription = data?.subscription;

  const isLegal = subscription?.identity.type === IdentityType.COMPANY;

  const address = isLegal
    ? subscription?.identity?.companyIdentity?.address
    : subscription?.identity?.individualIdentity?.address;

  const name = `${subscription?.identity.name}${
    isLegal ? ` (${subscription?.identity.identifier})` : ''
  }`;

  const birthdate = subscription?.identity.individualIdentity?.birthdate;

  return (
    <Drawer size="sm" onClose={onClose} {...props}>
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerBody pb="16" px="0">
          <Stack py="4" spacing="4">
            {!subscription || loading ? (
              <SkeletonText mx="3" noOfLines={5} skeletonHeight={3} width="70%" />
            ) : (
              <IdentityCard px="4">
                <IdentityAvatar
                  boxSize="14"
                  src={subscription.identity.profilePicture}
                  type={subscription.identity.type}
                />
                <Stack spacing="0">
                  <IdentityCardTitle isChecked={subscription.identity.isVerified}>
                    <Text>{name}</Text>
                  </IdentityCardTitle>
                  <IdentityCardDescription>
                    {birthdate && <Text>{formatDate(birthdate)}</Text>}
                    <Text>{address?.formatted}</Text>
                    <Text>{subscription.identity.email}</Text>
                    <Text>{subscription.identity.phone}</Text>
                  </IdentityCardDescription>
                </Stack>
              </IdentityCard>
            )}
            <Divider />
            <SubscriptionDetailsInvestAmount subscriptionId={subscriptionId} />
            <Divider />
            <SubscriptionProgressSteps subscriptionId={subscriptionId} />
            <Divider />
            <SubscriptionDetailsDocuments subscriptionId={subscriptionId} />
            <Divider />
            <SubscriptionDetailsHoldingMethod subscriptionId={subscriptionId} />
            <SubscriptionDetailsReminder subscriptionId={subscriptionId} />
            <Divider />
            <SubscriptionDetailsActivityFeed subscriptionId={subscriptionId} />
            <Divider />
            <SubscriptionDetailsTags subscriptionId={subscriptionId} />
            <Divider />
            <SubscriptionDetailsComments subscriptionId={subscriptionId} />
          </Stack>
          <DeleteSubscriptionButton subscriptionId={subscriptionId} onDelete={onDeleteRow} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export type SubscriptionDetailsSidePanelProps = Props;
